import { supabase } from '@/integrations/supabase/client';

interface StartupTask {
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  category: string;
  day: number;
  tip: string;
}

export class StartupTaskGenerator {
  private readonly STARTUP_TASKS: StartupTask[] = [
    // Week 1: Foundation & MVP Definition (Days 1-7)
    {
      day: 1,
      title: "Define your MVP scope",
      description: "List the core 3 features your product needs to solve the main problem",
      priority: "high",
      category: "mvp_planning",
      tip: "Focus on the ONE thing your product does better than anyone else. Everything else is secondary. Use tools like Figma for wireframing from Useful Tools section."
    },
    {
      day: 2,
      title: "Create your value proposition",
      description: "Write a clear 1-sentence value proposition that explains what you do and for whom",
      priority: "high",
      category: "strategy",
      tip: "Follow this format: 'We help [target audience] achieve [desired outcome] by [unique approach]'. Test it with 5 people to see if they understand it immediately."
    },
    {
      day: 3,
      title: "Research your target market",
      description: "Identify and research your ideal customer segments and market size",
      priority: "high",
      category: "market_research",
      tip: "Use Google Trends, Reddit, and industry reports. Look for communities where your target audience hangs out. Join Facebook groups and Discord servers."
    },
    {
      day: 4,
      title: "Analyze top 3 competitors",
      description: "Study your main competitors' features, pricing, and customer feedback",
      priority: "medium",
      category: "competitive_analysis",
      tip: "Read their negative reviews on G2, Capterra, or app stores. These pain points are your opportunities to differentiate."
    },
    {
      day: 5,
      title: "Create user personas",
      description: "Develop 2-3 detailed user personas based on your research",
      priority: "medium",
      category: "user_research",
      tip: "Include demographics, pain points, goals, and preferred communication channels. Give them names and faces to make them feel real."
    },
    {
      day: 6,
      title: "Design your MVP wireframes",
      description: "Create simple wireframes or mockups of your core features",
      priority: "high",
      category: "design",
      tip: "Keep it simple! Use Figma, Sketch, or even pen and paper. Focus on user flow, not pretty colors. Check design tools in Useful Tools."
    },
    {
      day: 7,
      title: "Plan your tech stack",
      description: "Choose the technologies and tools you'll use to build your MVP",
      priority: "medium",
      category: "technical_planning",
      tip: "For non-coders: Use no-code tools like Bubble, Webflow, or Airtable. For coders: Choose familiar technologies. Check dev tools in Useful Tools section."
    },

    // Week 2: Validation & Landing Page (Days 8-14)
    {
      day: 8,
      title: "Create a landing page",
      description: "Build a simple landing page explaining your solution and collecting emails",
      priority: "high",
      category: "marketing",
      tip: "Use tools like Carrd, Webflow, or Framer. Include problem, solution, and email signup. Keep it simple - one page is enough initially."
    },
    {
      day: 9,
      title: "Set up analytics tracking",
      description: "Install Google Analytics and other tracking tools on your landing page",
      priority: "medium",
      category: "analytics",
      tip: "Track visitor behavior, conversion rates, and traffic sources. Use Google Analytics, Hotjar for heatmaps, or simple tools like Plausible."
    },
    {
      day: 10,
      title: "Interview 5 potential customers",
      description: "Conduct customer interviews to validate your problem and solution",
      priority: "high",
      category: "customer_validation",
      tip: "Ask about their current solutions and pain points. Don't pitch - just listen. Use Community Pods to find interview subjects."
    },
    {
      day: 11,
      title: "Create social media presence",
      description: "Set up business profiles on 2-3 relevant social platforms",
      priority: "medium",
      category: "marketing",
      tip: "Choose platforms where your audience is active. LinkedIn for B2B, Instagram/TikTok for consumer products. Post regularly using Social Tracker."
    },
    {
      day: 12,
      title: "Build an email list",
      description: "Set up email marketing and create your first newsletter",
      priority: "medium",
      category: "marketing",
      tip: "Use Mailchimp, ConvertKit, or Beehiiv. Send weekly updates about your journey. People love following startup stories."
    },
    {
      day: 13,
      title: "Create your brand identity",
      description: "Design a logo and choose colors and fonts for your brand",
      priority: "low",
      category: "branding",
      tip: "Keep it simple initially. Use Canva, LogoMaker, or hire from Community Pods. Consistency matters more than perfection."
    },
    {
      day: 14,
      title: "Test pricing assumptions",
      description: "Research pricing models and test willingness to pay with potential customers",
      priority: "high",
      category: "pricing",
      tip: "Ask 'What would be too expensive?' and 'What would be so cheap you'd question the quality?' The right price is usually in between."
    },

    // Week 3: MVP Development (Days 15-21)
    {
      day: 15,
      title: "Start MVP development",
      description: "Begin building your minimum viable product focusing on core features only",
      priority: "high",
      category: "development",
      tip: "Start with the simplest version that solves the core problem. For SaaS: use tools like Bubble, Supabase, or Vercel. Check dev tools in Useful Tools."
    },
    {
      day: 16,
      title: "Set up user authentication",
      description: "Implement user registration, login, and basic user management",
      priority: "high",
      category: "development",
      tip: "Use services like Auth0, Supabase Auth, or Firebase Auth. Don't build authentication from scratch - it's complex and time-consuming."
    },
    {
      day: 17,
      title: "Build core feature #1",
      description: "Develop the most important feature that solves your main problem",
      priority: "high",
      category: "development",
      tip: "Focus on functionality over polish. Make it work first, make it pretty later. Get user feedback as soon as possible."
    },
    {
      day: 18,
      title: "Set up payment processing",
      description: "Integrate payment system (Stripe, PayPal) even if you're not charging yet",
      priority: "medium",
      category: "development",
      tip: "Having payments ready reduces friction later. Use Stripe for most use cases. Set up webhooks for subscription management."
    },
    {
      day: 19,
      title: "Create user onboarding flow",
      description: "Design and implement how new users will get started with your product",
      priority: "medium",
      category: "user_experience",
      tip: "Make the first 5 minutes magical. Guide users to their first 'aha moment' as quickly as possible. Use progressive disclosure."
    },
    {
      day: 20,
      title: "Implement basic analytics",
      description: "Add user behavior tracking and key metrics measurement",
      priority: "medium",
      category: "analytics",
      tip: "Track user actions, feature usage, and conversion funnels. Use tools like Mixpanel, Amplitude, or simple custom events."
    },
    {
      day: 21,
      title: "Test MVP internally",
      description: "Thoroughly test all features and fix critical bugs",
      priority: "high",
      category: "quality_assurance",
      tip: "Test on different devices and browsers. Create a simple testing checklist. Fix show-stopper bugs but don't aim for perfection."
    },

    // Week 4: Launch & Growth (Days 22-30)
    {
      day: 22,
      title: "Launch beta version",
      description: "Release your MVP to a small group of early users for feedback",
      priority: "high",
      category: "launch",
      tip: "Start with 10-50 beta users. Use your email list and Community Pods. Collect feedback actively and iterate quickly."
    },
    {
      day: 23,
      title: "Gather user feedback",
      description: "Collect and analyze feedback from your beta users",
      priority: "high",
      category: "user_feedback",
      tip: "Use tools like Typeform, UserVoice, or simple Google Forms. Ask specific questions about their experience and pain points."
    },
    {
      day: 24,
      title: "Iterate based on feedback",
      description: "Make improvements to your product based on user feedback",
      priority: "high",
      category: "product_iteration",
      tip: "Focus on the most common feedback themes. Don't try to please everyone - build for your core users first."
    },
    {
      day: 25,
      title: "Create content marketing strategy",
      description: "Plan blog posts, videos, or other content to attract customers",
      priority: "medium",
      category: "content_marketing",
      tip: "Write about problems your audience faces, not your product. Share your startup journey. Use SEO tools to find relevant topics."
    },
    {
      day: 26,
      title: "Launch public version",
      description: "Make your product publicly available and announce the launch",
      priority: "high",
      category: "launch",
      tip: "Post on Product Hunt, Reddit, Hacker News, and your social media. Have everything ready before announcing - you only get one first impression."
    },
    {
      day: 27,
      title: "Set up customer support",
      description: "Create help documentation and support channels for users",
      priority: "medium",
      category: "customer_support",
      tip: "Use tools like Intercom, Crisp, or simple email. Create FAQs for common questions. Respond quickly - customer service is a competitive advantage."
    },
    {
      day: 28,
      title: "Start paid marketing campaigns",
      description: "Launch targeted ads on platforms where your audience is active",
      priority: "medium",
      category: "paid_marketing",
      tip: "Start small with $5-10/day budgets. Test Facebook/Instagram ads, Google Ads, or LinkedIn ads. Focus on one platform initially."
    },
    {
      day: 29,
      title: "Analyze key metrics",
      description: "Review your analytics and plan improvements based on data",
      priority: "high",
      category: "analytics",
      tip: "Track user acquisition, activation, retention, and revenue. Use your ROI Tracker to monitor costs vs. revenue. Make data-driven decisions."
    },
    {
      day: 30,
      title: "Plan next sprint",
      description: "Based on learnings, plan your next 30-day development cycle",
      priority: "high",
      category: "strategy",
      tip: "Celebrate your progress! Review what worked and what didn't. Set goals for the next sprint based on user feedback and business metrics."
    }
  ];

  /**
   * Generates and saves 30-day startup task sequence for a user
   */
  async generateStartupTasks(userId: string): Promise<boolean> {
    try {
      const today = new Date();
      const tasks = this.STARTUP_TASKS.map(task => ({
        user_id: userId,
        title: task.title,
        description: task.description,
        priority: task.priority,
        category: task.category,
        task_type: 'startup_methodology',
        due_date: this.calculateDueDate(today, task.day),
        completed: false,
        sprint_day: task.day
      }));

      const { error } = await supabase
        .from('tasks')
        .insert(tasks);

      if (error) {
        console.error('Error generating startup tasks:', error);
        return false;
      }

      console.log(`Generated ${tasks.length} startup methodology tasks for user`);
      return true;
    } catch (error) {
      console.error('Error in generateStartupTasks:', error);
      return false;
    }
  }

  /**
   * Get task tip for a specific task title
   */
  getTaskTip(taskTitle: string): string {
    const task = this.STARTUP_TASKS.find(t => t.title === taskTitle);
    return task?.tip || 'Break this down into smaller actionable steps for better progress tracking';
  }

  /**
   * Calculate due date for a task based on sprint day
   */
  private calculateDueDate(startDate: Date, sprintDay: number): string {
    const dueDate = new Date(startDate);
    dueDate.setDate(startDate.getDate() + sprintDay - 1);
    return dueDate.toISOString().split('T')[0];
  }

  /**
   * Check if user already has startup methodology tasks
   */
  async hasStartupTasks(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('id')
        .eq('user_id', userId)
        .eq('task_type', 'startup_methodology')
        .limit(1);

      if (error) {
        console.error('Error checking startup tasks:', error);
        return false;
      }

      return (data?.length || 0) > 0;
    } catch (error) {
      console.error('Error in hasStartupTasks:', error);
      return false;
    }
  }
}

export const startupTaskGenerator = new StartupTaskGenerator();